import { useAsync, useAsyncCallback } from '@wix/cli-ui-kit';
import type { LooseAppManifest } from '@wix/cli-app-manifest';
import { useDevCenterOverridesClient } from '@wix/dev-center-overrides-client';
import { isInsideGitRepo, getGitLatestCommitHash } from '@wix/package-manager';
import { useProjectModel } from '../../../model.js';
import { assertBuildOutputExists } from '../../../files/assert-build-output-exists.js';
import { processAppManifest } from '../../../app-manifest/file.js';
import { useLoadBuildFiles } from './useLoadBuildFiles.js';
import { useUpdateManifest } from './useUpdateManifest.js';
import { useDeployFiles } from './useDeployFiles.js';

export interface PreviewResult {
  previewId: string;
  previewUrl?: string;
  appManifest: LooseAppManifest;
}

export function useCreatePreview({
  baseUrl,
  label,
  previewId: existingPreviewId,
  onPreviewCreated,
}: {
  baseUrl?: string;
  label?: string;
  previewId?: string;
  onPreviewCreated?: (result: PreviewResult) => void;
}) {
  const { model } = useProjectModel();

  const devCenterOverrides = useDevCenterOverridesClient();
  const loadBuildFiles = useLoadBuildFiles();
  const deployFiles = useDeployFiles();
  const { updateManifestWithBackendWorker } = useUpdateManifest();

  const uploadApplicationAction = useAsyncCallback(
    async (_, baseUrl?: string) => {
      if (baseUrl) {
        return {
          deploymentUrl: baseUrl,
        };
      }

      const buildFiles = await loadBuildFiles.execute();
      const { deploymentBaseUrl, deploymentId } =
        await deployFiles.execute(buildFiles);

      return {
        deploymentUrl: deploymentBaseUrl,
        deploymentId,
      };
    }
  );

  const createPreviewIdAction = useAsyncCallback(
    async (_, serverUrl: string, deploymentId?: string) => {
      const gitCommitHash = (await isInsideGitRepo(model.projectFolder))
        ? await getGitLatestCommitHash(model.projectFolder)
        : undefined;

      const { appManifest: rawManifest, componentPlaceholderFieldMasks } =
        await processAppManifest({
          projectFolder: model.projectFolder,
          config: model.config,
          serverUrl,
        });

      const appManifest = deploymentId
        ? updateManifestWithBackendWorker(
            rawManifest,
            deploymentId,
            serverUrl,
            label,
            gitCommitHash
          )
        : rawManifest;

      let previewId: string;
      if (existingPreviewId) {
        previewId = await devCenterOverrides.updateOverrideWithNoSideEffects({
          id: existingPreviewId,
          appManifest,
          componentFieldMasks: componentPlaceholderFieldMasks,
          projectId: model.releaseAppId,
        });
      } else {
        previewId = await devCenterOverrides.createComponentsOverride({
          appManifest,
          projectId: model.releaseAppId,
        });
      }

      return { previewId, appManifest };
    }
  );

  const { status } = useAsync(async () => {
    await assertBuildOutputExists(model.projectFolder, model.config);

    const { deploymentUrl: previewUrl, deploymentId } =
      await uploadApplicationAction.execute(baseUrl);

    const { previewId, appManifest } = await createPreviewIdAction.execute(
      previewUrl,
      deploymentId
    );

    const result = { previewId, previewUrl, appManifest };
    onPreviewCreated?.(result);

    return result;
  }, []);

  return {
    uploadStatus: uploadApplicationAction.status,
    createPreviewStatus: createPreviewIdAction.status,
    status,
  };
}
